// === INPACT:SLUITSTRAAT:SAA:APP:v2 ===
// Using @sluitstraat/core for shared components

import React, { useEffect, useMemo, Suspense } from 'react';
import { BrowserRouter, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ConfigProvider,
  useConfig,
  WorkflowProvider,
  ThemeProvider,
  ErrorBoundary,
  LoadingScreen,
  HeaderExtrasProvider,
  setWorkflowConfig,
} from '@sluitstraat/core';
import Router from './Router';
import { saaWorkflowConfig, buildWorkflowConfig, setActiveProductOrder, resolveProductOrder } from './workflowConfig';
import { BrandSelector } from './components/BrandSelector';
import { SaaShellNavigation } from './components/SaaShellNavigation';
import { appStyles } from './styles/commonStyles';

// Initialize customer-specific workflow configuration at app startup
setWorkflowConfig(saaWorkflowConfig);

/**
 * Preserves the ?brand= query parameter across navigations.
 * When a brand is stored in sessionStorage but missing from the URL,
 * this does a replace-navigation to restore it.
 */
const BrandQueryPreserver: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const brandInUrl = searchParams.get('brand');
    if (brandInUrl) return; // Already present, nothing to do

    try {
      const storedBrand = sessionStorage.getItem('sluitstraat_brand');
      if (storedBrand) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('brand', storedBrand);
        navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
      }
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on pathname on purpose: it restores ?brand after each navigation, and searchParams/navigate would re-fire on its own replace
  }, [location.pathname]);

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { config, isLoading, error } = useConfig();
  const { t } = useTranslation();

  useMemo(() => {
    if (isLoading || error || !config) return;
    const order = resolveProductOrder(
      (config.workflow as { productOrder?: string[] } | undefined)?.productOrder,
    );
    setActiveProductOrder(order);
    setWorkflowConfig(buildWorkflowConfig(order));
  }, [config, isLoading, error]);

  useEffect(() => {
    if (!isLoading && !error) {
      // Apply browser title from texts config, fallback to branding
      document.title = config.texts?.general?.browserTitle || config.branding.appName;

      // Apply brand-specific favicon
      const faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (faviconLink && config.branding.faviconUrl) {
        faviconLink.href = config.branding.faviconUrl;
      }

      // Apply CSS variables for theming
      const b = config.branding;
      const s = document.documentElement.style;
      s.setProperty('--primary-color', b.primaryColor);
      s.setProperty('--secondary-color', b.secondaryColor);
      s.setProperty('--accent-color', b.accentColor || '#09B4B4');
      s.setProperty('--background-color', b.backgroundColor || '#FFFFFF');
      s.setProperty('--text-color', b.textColor || '#373F47');
      s.setProperty('--foreground', b.textColor || '#373F47');
      s.setProperty('--font-family', b.fontFamily || 'Montserrat, Helvetica, Arial, sans-serif');
      s.setProperty('--font-primary', b.fontFamily || 'Montserrat, Helvetica, Arial, sans-serif');

      // Extended palette
      s.setProperty('--primary-light', b.primaryLight || '#CBE0DE');
      s.setProperty('--primary-200', b.primary200 || '#09B4B4');
      s.setProperty('--primary-400', b.primary400 || '#078D8D');
      s.setProperty('--primary-600', b.primary600 || '#023131');
      s.setProperty('--secondary-200', b.secondary200 || '#FA8F8C');
      s.setProperty('--secondary-400', b.secondary400 || '#F7423E');
      s.setProperty('--background-dark', b.backgroundDark || '#F5F5F5');
      s.setProperty('--background', b.backgroundDark || '#F5F5F5');
      s.setProperty('--text-heading', b.textHeading || '#212426');
      s.setProperty('--text-secondary', b.textSecondary || '#767676');
      s.setProperty('--success-color', b.successColor || '#28A745');
      s.setProperty('--completed-step-color', b.primaryColor);
      s.setProperty('--completed-step-bg', b.primaryColor);
      s.setProperty('--warning-color', b.warningColor || '#FFC107');
      s.setProperty('--error-color', b.errorColor || '#F51C17');
      s.setProperty('--card-bg', b.cardBg || '#FFFFFF');
      s.setProperty('--card-border', b.cardBorder || '#e2e8f0');
      s.setProperty('--surface-muted', b.surfaceMuted || '#f7f9fc');
      s.setProperty('--input-bg', b.inputBg || '#FFFFFF');
      s.setProperty('--input-text', b.inputText || '#373F47');
      s.setProperty('--input-border', b.inputBorder || '#e2e8f0');
      s.setProperty('--input-placeholder', b.inputPlaceholder || '#767676');
      s.setProperty('--header-bg', b.headerBg || '#FFFFFF');
      s.setProperty('--header-text', b.headerText || '#212426');
      s.setProperty('--footer-bg', b.footerBg || '#FFFFFF');
      s.setProperty('--footer-text', b.footerTextColor || '#767676');
      s.setProperty('--scrollbar-thumb', b.scrollbarThumb || '#056666');
      s.setProperty('--shadow-color', b.shadowColor || 'rgba(0, 0, 0, 0.1)');
      s.setProperty('--radius', b.radius || '5px');
      s.setProperty('--radius-sm', b.radiusSm || '5px');
      s.setProperty('--radius-lg', b.radiusLg || '15px');
      s.setProperty('--radius-pill', b.radiusPill || '50px');

      // Apply custom CSS if provided
      if (config.branding.customCSS) {
        const styleElement = document.getElementById('custom-css-from-config');
        if (styleElement) {
          styleElement.textContent = config.branding.customCSS;
        } else {
          const newStyleElement = document.createElement('style');
          newStyleElement.id = 'custom-css-from-config';
          newStyleElement.textContent = config.branding.customCSS;
          document.head.appendChild(newStyleElement);
        }
      }

      // Select dropdown option styling lives in index.css (single source).
    }
  }, [config, isLoading, error]);

  if (isLoading) {
    return <LoadingScreen message={t('messages.configLoading')} />;
  }

  if (error) {
    return (
      <div style={appStyles.configErrorBox}>
        <h1 style={appStyles.configErrorTitle}>{t('errors.configError')}</h1>
        <p>{t('errors.configErrorDescription')}</p>
        <p style={appStyles.configErrorMessage}>{error.message}</p>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen message="Loading translations..." />}>
      <WorkflowProvider persistToSessionStorage={false}>
        <BrandQueryPreserver>
          <SaaShellNavigation>
            <Router />
          </SaaShellNavigation>
        </BrandQueryPreserver>
      </WorkflowProvider>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <ThemeProvider>
          <ConfigProvider>
            <HeaderExtrasProvider extraActions={<><BrandSelector /></>}>
              <AppContent />
            </HeaderExtrasProvider>
          </ConfigProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
